/**
 * Phase F2 — Buyer Distribution Center
 * -------------------------------------
 * Surfaces Phase 7 distribution participation rows grouped by lifecycle status.
 *
 * Endpoints used (read-only):
 *   GET /api/buyer/distribution-participation  — all participations across portfolios
 *
 * Buckets:
 *   GENERATED  → Generated
 *   APPROVED   → Approved
 *   PAID       → Ready For Payout (paid / ready_for_payout / disbursed)
 *   *          → Historical (closed/cancelled/expired/etc)
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DollarSign, FileText, CheckCircle, Wallet, Archive,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'GENERATED', label: 'Generated', Icon: FileText, color: theme.colors.status.info,
    statuses: ['GENERATED'] },
  { key: 'APPROVED', label: 'Approved', Icon: CheckCircle, color: theme.colors.accent.teal,
    statuses: ['APPROVED'] },
  { key: 'READY_FOR_PAYOUT', label: 'Ready For Payout', Icon: Wallet, color: theme.colors.status.success,
    statuses: ['READY_FOR_PAYOUT', 'PAID', 'DISBURSED'] },
  { key: 'HISTORICAL', label: 'Historical', Icon: Archive, color: theme.colors.text.muted,
    statuses: null /* anything not in the others */ },
];

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function fmtMoney(amount, currency = 'USD') {
  const n = Number(amount) || 0;
  return `${n.toFixed(2)} ${currency}`;
}

const StatusPill = ({ status }) => {
  const color =
    status === 'GENERATED' ? theme.colors.status.info :
    status === 'APPROVED' ? theme.colors.accent.teal :
    (status === 'PAID' || status === 'READY_FOR_PAYOUT' || status === 'DISBURSED') ? theme.colors.status.success :
    theme.colors.text.muted;
  return (
    <span style={{
      display: 'inline-flex', padding: '3px 10px', borderRadius: 999,
      background: `${color}1A`, color, fontSize: 11, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: 0.4,
    }}>{status || '—'}</span>
  );
};

function bucketRow(row, tabKey) {
  const s = (row.status || '').toUpperCase();
  if (tabKey === 'HISTORICAL') {
    const known = ['GENERATED', 'APPROVED', 'READY_FOR_PAYOUT', 'PAID', 'DISBURSED'];
    return !known.includes(s);
  }
  const tab = TABS.find((t) => t.key === tabKey);
  return tab?.statuses?.includes(s);
}

export default function BuyerDistributionCenter() {
  const [activeTab, setActiveTab] = useState('GENERATED');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/buyer/distribution-participation`, auth);
        if (cancelled) return;
        setRows(res.data?.participation || []);
        setTotal(res.data?.total_gross_participation || 0);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load distributions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [auth]);

  const counts = useMemo(() => {
    const map = { GENERATED: 0, APPROVED: 0, READY_FOR_PAYOUT: 0, HISTORICAL: 0 };
    rows.forEach((r) => {
      for (const k of Object.keys(map)) {
        if (bucketRow(r, k)) map[k] += 1;
      }
    });
    return map;
  }, [rows]);

  const visible = useMemo(
    () => rows.filter((r) => bucketRow(r, activeTab)),
    [rows, activeTab],
  );

  return (
    <div data-testid="buyer-distribution-center-page" style={{ padding: 24, maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <DollarSign size={26} color={theme.colors.primary} />
          Distribution Center
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Track your distribution participations across portfolios.
        </p>
      </div>

      <div data-testid="distribution-total-card" style={{
        ...cardStyle, padding: 18, marginBottom: 18,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: 12, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Total Gross Participation
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: theme.colors.text.primary, marginTop: 4 }}>
            {fmtMoney(total)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 18 }}>
          {TABS.map((t) => (
            <div key={t.key} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t.label}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.color }}>
                {counts[t.key] || 0}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div data-testid="distribution-tabs" style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {TABS.map((t) => {
          const Icon = t.Icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`distribution-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: theme.radius.cta,
                border: `1px solid ${active ? t.color : theme.colors.borders}`,
                background: active ? `${t.color}15` : theme.colors.bg.card,
                color: active ? t.color : theme.colors.text.secondary,
                cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              <Icon size={16} /> {t.label}
              <span style={{
                marginLeft: 4, background: active ? t.color : theme.colors.bg.tertiary,
                color: active ? '#fff' : theme.colors.text.secondary,
                padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              }}>{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div data-testid="distribution-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading distributions…
        </div>
      ) : visible.length === 0 ? (
        <div data-testid="distribution-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.secondary }}>
          No distributions in this bucket.
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['Portfolio', 'Distribution ID', 'Model', 'Amount', 'Status', 'Date'].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '12px 14px',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr
                  key={r.distribution_id}
                  data-testid={`distribution-row-${r.distribution_id}`}
                  style={{ borderTop: `1px solid ${theme.colors.borders}` }}
                >
                  <td style={{ padding: '12px 14px', fontWeight: 600, color: theme.colors.text.primary }}>
                    {r.portfolio_name || r.portfolio_id}
                  </td>
                  <td style={{ padding: '12px 14px', color: theme.colors.text.secondary, fontFamily: 'monospace', fontSize: 12 }}>
                    {(r.distribution_id || '').slice(0, 12)}…
                  </td>
                  <td style={{ padding: '12px 14px', color: theme.colors.text.secondary }}>
                    {r.distribution_model || '—'}
                  </td>
                  <td style={{ padding: '12px 14px', fontWeight: 700, color: theme.colors.text.primary }}>
                    {fmtMoney(r.gross_amount, r.currency)}
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <StatusPill status={r.status} />
                  </td>
                  <td style={{ padding: '12px 14px', color: theme.colors.text.secondary }}>
                    {fmtDate(r.generated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
